import { Fragment, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { Skeleton, Typography } from '@mui/material';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  GetUserAccountByIdResult,
  UserAccountSummary,
} from '../../../../../utility/types/dataProvider';
import { UserAvatar } from '../../../../UserAvatar/UserAvatar';
import { DefaultSavedSearchResult } from 'providers/contexts/extensions/SavedSearches';

type SearchSavedUserFieldProps = {
  record: DefaultSavedSearchResult['data']; // saved search result item
};

export const SearchSavedUserField = memo(({ record }: SearchSavedUserFieldProps) => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const { data, isLoading } = useQuery<
    GetUserAccountByIdResult,
    ApiErrorResponse,
    GetUserAccountByIdResult
  >({
    queryKey: ['getUserAccountSummaryById', record?.user_id],
    queryFn: async () => {
      if (!record?.user_id) {
        return;
      }
      return await dataProvider.getUserAccountSummaryById(record.user_id);
    },
    enabled: Boolean(record?.user_id),
    gcTime: 30000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const user = data?.data as UserAccountSummary;

  const owner = (
    <Fragment>
      {(user?.first_name || user?.last_name) && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
          }}
        >
          {`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        </Typography>
      )}
      {user?.email && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
          }}
        >
          {String(user?.email).trim()}
        </Typography>
      )}
      {user?.screen_name && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
          }}
        >
          {String(user?.screen_name).trim()}
        </Typography>
      )}
    </Fragment>
  );

  if (isLoading) {
    return (
      <Skeleton
        variant="circular"
        width={30}
        height={30}
      />
    );
  }

  return (
    <UserAvatar
      user={user}
      size={30}
      placement="top"
      customTitle={owner}
    />
  );
});

SearchSavedUserField.displayName = 'SearchSavedUserField';

export default SearchSavedUserField;
